import {
    IconButton,
    Button,
    HStack,
    Icon,
    StackDivider,
    Text,
    Tooltip,
    VStack,
    MenuItem,
    MenuButton,
    MenuList,
    Menu,
    Spinner,
    MenuDivider,
    Wrap,
} from '@chakra-ui/react';
import { ReportingViewBaseConfig } from '../../../../base';
import { AiOutlinePlus } from 'react-icons/ai';
import { createDatabaseFilterView } from '../../editor/filter';
import { FormCollectionViewProps } from './formCollectionProps';
import { ValueItemContainerProps } from '../../../common';
import {
    CollectionItemContainerProps,
    CollectionItemViewProps,
} from '../../../common/collection/item';
import { HiOutlineDotsVertical, HiOutlineExternalLink } from 'react-icons/hi';

export function createFormCollectionView(
    config: ReportingViewBaseConfig & {
        Container: {
            Value: {
                Item: React.FC<ValueItemContainerProps>;
            };
            Collection: {
                Item: React.FC<CollectionItemContainerProps>;
            };
        };
    }
): React.FC<FormCollectionViewProps> {
    const {
        UI: {
            View: { createMenu },
        },
        Container: {
            Property: { Filter: FilterContainer },
            Value: { Item: ValueItemContainer },
            Collection: { Item: CollectionItemContainer },
        },
    } = config;

    const View = { Menu: createMenu() };
    const FilterItem = createDatabaseFilterView(config);

    const CollectionItem: React.FC<CollectionItemViewProps> = (props) => {
        return (
            <HStack
                {...props.container.element}
                w="full"
                justify="space-between"
                position="relative"
            >
                <HStack
                    position="absolute"
                    left={-8}
                    {...(props.menu.isVisible
                        ? {
                              opacity: 1,
                          }
                        : {
                              opacity: 0,
                          })}
                >
                    <View.Menu {...props.menu} minW="16rem">
                        <IconButton
                            onFocus={(event) => event.preventDefault()}
                            aria-label="Open menu"
                            size="xs"
                            bg="none"
                            icon={
                                <Icon
                                    as={HiOutlineDotsVertical}
                                    color="whiteAlpha.500"
                                    fontSize="lg"
                                />
                            }
                            _hover={{
                                bg: 'whiteAlpha.100',
                            }}
                            _expanded={{
                                bg: 'whiteAlpha.100',
                            }}
                            _active={{
                                bg: 'none',
                            }}
                            _focus={{
                                outline: 'none',
                            }}
                            _focusVisible={{
                                outline: 'none',
                            }}
                        />
                    </View.Menu>
                </HStack>
                <HStack
                    flex={1}
                    onClick={props.open.container.onClick}
                    w="full"
                    align="center"
                    spacing={16}
                    px={2}
                    h={10}
                    borderRadius="md"
                    justify="space-between"
                    userSelect="none"
                    cursor="pointer"
                    _hover={{ bg: 'whiteAlpha.50' }}
                >
                    <HStack spacing={3} minW="12rem">
                        {props.icon.type === 'emoji' && <Text>{props.icon.emoji}</Text>}
                        {props.icon.type === 'icon' && (
                            <Icon fontSize="lg" as={props.icon.Component} />
                        )}
                        <Text
                            fontWeight="semibold"
                            noOfLines={1}
                            color="whiteAlpha.800"
                            wordBreak="break-all"
                        >
                            {props.label}
                        </Text>
                        {props.open.button && (
                            <Button
                                {...props.open.button}
                                size="sm"
                                bg="none"
                                minH={7}
                                h={7}
                                px={2}
                                flexShrink={0}
                                color="whiteAlpha.400"
                                leftIcon={
                                    <Icon as={HiOutlineExternalLink} fontSize="md" />
                                }
                                _hover={{
                                    bg: 'whiteAlpha.100',
                                    color: 'whiteAlpha.500',
                                }}
                                _active={{
                                    bg: 'whiteAlpha.100',
                                    color: 'whiteAlpha.500',
                                }}
                                _focus={{
                                    outline: 'none',
                                }}
                                _focusVisible={{
                                    outline: 'none',
                                }}
                            >
                                Open
                            </Button>
                        )}
                    </HStack>
                    <HStack
                        color="whiteAlpha.600"
                        flexShrink={0}
                        fontSize="sm"
                        spacing={0}
                        h="full"
                    >
                        {props.properties.map((property) => (
                            <Tooltip
                                key={property.id}
                                placement="top"
                                openDelay={300}
                                px={2}
                                py={1}
                                bg="gray.50"
                                color="gray.900"
                                borderRadius="md"
                                shouldWrapChildren={true}
                                label={property.property.label}
                            >
                                <HStack h="full" px={1} _hover={{ bg: 'whiteAlpha.50' }}>
                                    <ValueItemContainer
                                        key={property.id}
                                        {...property.value}
                                    />
                                </HStack>
                            </Tooltip>
                        ))}
                    </HStack>
                </HStack>
            </HStack>
        );
    };

    return (props) => {
        return (
            <VStack
                w="full"
                align="start"
                spacing={4}
                divider={<StackDivider borderColor="whiteAlpha.50" borderWidth={1} />}
                role="group"
                py={{ base: 4, md: 4, xl: 8, '2xl': 12 }}
            >
                <HStack w="full" justify="space-between" align="end" userSelect="none">
                    <HStack spacing={1} w="full">
                        <Button
                            flexShrink={0}
                            size="sm"
                            minH={7}
                            h={7}
                            bg="none"
                            _hover={{
                                bg: 'whiteAlpha.100',
                            }}
                            color="whiteAlpha.800"
                            leftIcon={
                                <Icon
                                    fontSize="lg"
                                    color="whiteAlpha.800"
                                    as={props.collection.layout.button.Icon}
                                />
                            }
                        >
                            {props.collection.layout.button.label}
                        </Button>
                        <Text fontWeight="medium" fontSize="sm" color="whiteAlpha.600">
                            {props.collection.collection.total.label}
                        </Text>
                    </HStack>
                    <HStack
                        spacing={3}
                        // opacity={0}
                        // transition="opacity 0.3s ease-in-out"
                        // _groupHover={{ opacity: 1 }}
                    >
                        <HStack spacing={1}>
                            {props.collection.status.isLoading && (
                                <IconButton
                                    isLoading={true}
                                    aria-label="Refreshing data"
                                    size="sm"
                                    // minH={7}
                                    // h={7}
                                    // minW={7}
                                    // w={7}
                                    color="whiteAlpha.500"
                                />
                            )}
                            {props.collection.actions.items.map((action) => (
                                <Tooltip
                                    key={action.id}
                                    placement="top"
                                    openDelay={300}
                                    px={2}
                                    py={1}
                                    bg="gray.50"
                                    color="gray.900"
                                    borderRadius="md"
                                    shouldWrapChildren={true}
                                    label={action.label}
                                >
                                    <IconButton
                                        {...action.button}
                                        aria-label={action.label}
                                        size="sm"
                                        // minH={7}
                                        // h={7}
                                        // minW={7}
                                        // w={7}
                                        bg="none"
                                        _hover={{
                                            bg: 'whiteAlpha.100',
                                        }}
                                        _active={{
                                            bg: 'none',
                                            _hover: {
                                                bg: 'whiteAlpha.100',
                                            },
                                        }}
                                        icon={
                                            <Icon
                                                aria-selected={action.button.isActive}
                                                fontSize="lg"
                                                color="whiteAlpha.600"
                                                as={action.Icon}
                                                _selected={{
                                                    color: 'blue.300',
                                                }}
                                            />
                                        }
                                    />
                                </Tooltip>
                            ))}
                            <Menu isLazy={true} placement="bottom">
                                <Tooltip
                                    {...props.collection.menu.tooltip}
                                    closeOnClick={true}
                                    placement="top"
                                    openDelay={300}
                                    px={2}
                                    py={1}
                                    bg="gray.50"
                                    color="gray.900"
                                    borderRadius="md"
                                    shouldWrapChildren={true}
                                >
                                    <MenuButton
                                        as={IconButton}
                                        onFocus={(event) => event.preventDefault()}
                                        size="sm"
                                        // minH={7}
                                        // h={7}
                                        // minW={7}
                                        // w={7}
                                        bg="none"
                                        _hover={{
                                            bg: 'whiteAlpha.100',
                                        }}
                                        _expanded={{
                                            bg: 'whiteAlpha.100',
                                        }}
                                        icon={
                                            <Icon
                                                fontSize="lg"
                                                color="whiteAlpha.600"
                                                as={props.collection.menu.Icon}
                                            />
                                        }
                                    />
                                </Tooltip>
                                <MenuList minW="16rem" bg="black" p={0} overflow="hidden">
                                    <VStack
                                        align="start"
                                        w="full"
                                        bg="whiteAlpha.200"
                                        p={1}
                                        spacing={0}
                                    >
                                        {props.collection.menu.items.map((item) => (
                                            <MenuItem
                                                key={item.id}
                                                as={Button}
                                                {...item.button}
                                                borderRadius="md"
                                                py={1}
                                                fontSize="sm"
                                                fontWeight="medium"
                                                color="whiteAlpha.800"
                                                icon={
                                                    item.Icon ? (
                                                        <Icon
                                                            fontSize="lg"
                                                            as={item.Icon}
                                                        />
                                                    ) : undefined
                                                }
                                                _hover={{
                                                    bg: 'whiteAlpha.50',
                                                    color: item.colorScheme
                                                        ? `${item.colorScheme}.400`
                                                        : undefined,
                                                }}
                                            >
                                                {item.label}
                                            </MenuItem>
                                        ))}
                                        <MenuDivider
                                            my={1}
                                            bg="whiteAlpha.100"
                                            borderColor="whiteAlpha.200"
                                            w="full"
                                        />
                                        <MenuItem
                                            py={1}
                                            borderRadius="md"
                                            userSelect="none"
                                            color="whiteAlpha.500"
                                            cursor="default"
                                            _hover={{ bg: 'none' }}
                                            fontSize="xs"
                                        >
                                            <VStack align="start" w="full" spacing={0}>
                                                <Text>
                                                    {
                                                        props.collection.status
                                                            .lastUpdated.label
                                                    }
                                                </Text>
                                            </VStack>
                                        </MenuItem>
                                    </VStack>
                                </MenuList>
                            </Menu>
                        </HStack>
                        <Button
                            {...props.collection.new.button}
                            size="sm"
                            colorScheme="blue"
                            bg="blue.500"
                            color="whiteAlpha.900"
                            fontWeight="semibold"
                            iconSpacing={1}
                            _hover={{
                                bg: 'blue.600',
                            }}
                            _active={{
                                bg: 'blue.700',
                            }}
                            leftIcon={<Icon as={AiOutlinePlus} fontSize="sm" />}
                        >
                            {props.collection.new.label}
                        </Button>
                    </HStack>
                </HStack>
                <VStack w="full" align="start" spacing={4}>
                    {props.collection.list.items.length === 0 && (
                        <HStack
                            w="full"
                            justify="center"
                            p={6}
                            bg="whiteAlpha.50"
                            borderRadius="md"
                        >
                            <Text color="whiteAlpha.500" fontWeight="medium">
                                No items found
                            </Text>
                        </HStack>
                    )}
                    {props.collection.filter.isVisible && (
                        <Wrap
                            shouldWrapChildren={true}
                            spacing={2}
                            w="full"
                            fontSize="sm"
                        >
                            {props.collection.filter.suggestions.map((item) => (
                                <FilterContainer
                                    key={item.property.key}
                                    as={FilterItem}
                                    {...item}
                                />
                            ))}
                            {props.collection.filter.items.map((item) => (
                                <HStack
                                    key={item.id}
                                    flexShrink={0}
                                    minH={7}
                                    h={7}
                                    px={3}
                                    bg="none"
                                    fontWeight="semibold"
                                    borderWidth={1}
                                    borderColor="blue.300"
                                    borderRadius="full"
                                    _hover={{
                                        bg: 'whiteAlpha.100',
                                    }}
                                >
                                    <Text>{item.label}</Text>
                                </HStack>
                            ))}
                            <Button
                                flexShrink={0}
                                minH={7}
                                h={7}
                                px={2}
                                bg="none"
                                fontSize="sm"
                                fontWeight="medium"
                                color="whiteAlpha.500"
                                leftIcon={<Icon as={AiOutlinePlus} />}
                                borderRadius="full"
                                _hover={{
                                    bg: 'whiteAlpha.100',
                                }}
                            >
                                Filter
                            </Button>
                        </Wrap>
                    )}
                    {props.collection.list.items.length > 0 && (
                        <VStack w="full" align="start" spacing={0}>
                            {props.collection.list2.items.map((item) => (
                                <CollectionItemContainer
                                    key={item.id}
                                    as={CollectionItem}
                                    {...item}
                                />
                            ))}
                        </VStack>
                    )}
                </VStack>
            </VStack>
        );
    };
}
